import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Pause } from 'lucide-react';

const ModulesCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const timeoutRef = useRef(null);

    const modulesData = [
        {
            letter: "S",
            name: "S.H.A.D.E",
            title: "Secure Hostile Adaptive Defence Engine.",
            description: "Maps the room in real-time. It recognizes group dynamics, detects curiosity, and adjusts the narrative depth instantly.",
            gradient: "from-blue-600 to-indigo-900",
            bgIconColor: "bg-blue-500"
        },
        {
            letter: "L",
            name: "Living Ancestor",
            title: "History, resurrected.",
            description: "Host hyper-realistic AI avatars that speak 100+ languages with zero latency. They don't just speak; they listen.",
            gradient: "from-purple-600 to-fuchsia-900",
            bgIconColor: "bg-purple-500"
        },
        {
            letter: "E",
            name: "Exhibit OS",
            title: "The OS for physical spaces.",
            description: "A refined, dashboard-centric interface to manage exhibits remotely, sync with FOZZIL ID, and deploy updates instantly.",
            gradient: "from-emerald-600 to-teal-900",
            bgIconColor: "bg-emerald-500"
        },
        {
            letter: "D",
            name: "Deep Analytics",
            title: "Know your audience.",
            description: "Privacy-first analytics that reveal visitor engagement patterns, dwell times, and emotional responses without capturing personal IDs.",
            gradient: "from-orange-600 to-red-900",
            bgIconColor: "bg-orange-500"
        },
        {
            letter: "G",
            name: "Generative Art",
            title: "Infinite creativity.",
            description: "Turn any surface into a canvas. Real-time generative visuals that react to sound, movement, and time of day.",
            gradient: "from-pink-600 to-rose-900",
            bgIconColor: "bg-pink-500"
        }
    ];

    const resetTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        resetTimeout();
        if (isPlaying) {
            timeoutRef.current = setTimeout(() => {
                setCurrentIndex((prevIndex) => 
                    prevIndex === modulesData.length - 1 ? 0 : prevIndex + 1
                );
            }, 5000);
        }
        return () => resetTimeout();
    }, [currentIndex, isPlaying, modulesData.length]);

    const handleDotClick = (index) => {
        setCurrentIndex(index);
        setIsPlaying(false);
    };

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    return (
        <div className="w-full relative group">
            <div className="relative h-[600px] md:h-[600px] w-full overflow-hidden rounded-[30px] md:rounded-[40px] border border-white/10 bg-zinc-900">
                <AnimatePresence mode='wait'>
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className="absolute inset-0 w-full h-full flex flex-col md:flex-row"
                    >
                        {/* Dynamic Background */}
                         <div className={`absolute inset-0 bg-gradient-to-br ${modulesData[currentIndex].gradient} opacity-20`} />
                        
                         {/* Left: Icon/Visual */}
                         <div className="flex-[0.8] md:flex-1 flex items-center justify-center p-6 md:p-12 relative z-10">
                            <motion.div 
                                initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
                                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                transition={{ type: "spring", duration: 0.8 }}
                                className={`w-36 h-36 md:w-64 md:h-64 rounded-[32px] md:rounded-[40px] ${modulesData[currentIndex].bgIconColor} flex items-center justify-center shadow-2xl skew-y-3`}
                            >
                                <span className="text-7xl md:text-9xl font-bold text-white drop-shadow-md">
                                    {modulesData[currentIndex].letter}
                                </span>
                            </motion.div>
                         </div>

                        {/* Right: Content */}
                        <div className="flex-1 flex flex-col justify-start md:justify-center p-6 md:p-16 relative z-10 text-center md:text-left">
                            <motion.span 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-white/60 font-medium text-xs md:text-xl uppercase tracking-widest mb-2 md:mb-4 block"
                            >
                                {modulesData[currentIndex].name}
                            </motion.span>
                            <motion.h2 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="text-2xl md:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight"
                            >
                                {modulesData[currentIndex].title}
                            </motion.h2>
                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="text-sm md:text-xl text-zinc-300 leading-relaxed max-w-lg mx-auto md:mx-0"
                            >
                                {modulesData[currentIndex].description}
                            </motion.p>
                        </div>
                    </motion.div>
                </AnimatePresence>

                 {/* Controls Overlay */}
                 <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4 bg-black/40 backdrop-blur-md px-6 py-3 rounded-full border border-white/10">
                    <button 
                        onClick={togglePlay} 
                        className="text-white hover:text-blue-400 transition-colors focus:outline-none"
                    >
                        {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
                    </button>
                    
                    <div className="h-4 w-px bg-white/20 mx-2" />

                    <div className="flex gap-3">
                        {modulesData.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => handleDotClick(index)}
                                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                                    index === currentIndex ? 'bg-white scale-125' : 'bg-white/30 hover:bg-white/50'
                                }`}
                            />
                        ))}
                    </div>
                 </div>
            </div>
        </div>
    );
};

export default ModulesCarousel;
